import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { useWidgetConfig } from "../../features/customization/hooks/use-widget-config";
import { useStakingRouteSync } from "../../features/staking/hooks/use-staking-route-sync";
import { assetRouteLoader } from "../../features/staking/utils/route-loaders";
import { resolveStakingAssetFromEnabled } from "../../features/staking/utils/staking-routing";
import { prefetchSdkStakingMetadata } from "../../shared/utils/metadata-data";

/**
 * Layout route for a specific staking asset.
 * Renders the asset index page via the .index child, and hosts stake/unstake/claim children.
 */
export const Route = createFileRoute("/_layout/staking/$asset")({
  loader: async (ctx) => {
    // Prefetch staking protocol metadata (fire and forget)
    await prefetchSdkStakingMetadata().catch(console.warn);
    return assetRouteLoader(ctx);
  },
  component: AssetLayout,
});

function AssetLayout() {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const { config } = useWidgetConfig();
  const { asset } = useParams({ from: "/_layout/staking/$asset" });
  const isEthereumStakingEnabled = config.features.staking.ethereum?.enabled !== false;
  const isSolanaStakingEnabled = config.features.staking.solana?.enabled !== false;

  useEffect(() => {
    const targetAsset = resolveStakingAssetFromEnabled(
      asset,
      isEthereumStakingEnabled,
      isSolanaStakingEnabled,
    );

    if (!targetAsset || targetAsset === asset) return;

    const updatedPath = routerLocation.pathname.replace(
      /^\/staking\/[^/]+/,
      `/staking/${targetAsset}`,
    );
    navigate({ to: updatedPath, replace: true });
  }, [asset, isEthereumStakingEnabled, isSolanaStakingEnabled, navigate, routerLocation.pathname]);

  useStakingRouteSync();
  return <Outlet />;
}
